import * as React from 'react';

import { compose } from 'recompose';

import { useLocation } from 'js/lib/useRouter';
import user, { isAuthenticatedUser } from 'js/lib/user';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { allContractsHardPausedOrTerminated } from 'bundles/enterprise-admin-contracts/enterprise-admin-contracts-index';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import ProgramArchivedDenyModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramArchivedDenyModal';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useEnterpriseContracts,
  useIsProgramLoading,
  useProgram,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useIsJitOrganization } from 'bundles/unified-common/hooks/useIsJitOrganization';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';
import { useProgramAutoJoinDomainAllowList } from 'bundles/unified-common/hooks/useProgramAutoJoinDomainAllowList';
import { useSsoLockedOutStatus } from 'bundles/unified-common/hooks/useSsoLockedOutStatus';
import { normalizeSearchString } from 'bundles/unified-common/utils/orgSearchQuery';
import { isWithinSsoGracePeriod } from 'bundles/unified-common/utils/ssoGracePeriod';

type Props = {
  naptime?: InjectedNaptime;
  children: React.ReactNode;
};

export const EnterprisePageGuard = ({ children, naptime }: Props): React.ReactNode | null => {
  const isOrgScopedHomeEnabled = useIsOrgScopedHome();
  const program = useProgram();
  const enterpriseContracts = useEnterpriseContracts();
  const isProgramLoading = useIsProgramLoading();
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const isFeaturesLoading = useEnterpriseProductFeaturesLoading();
  const thirdPartyOrg = useThirdPartyOrganization();
  const { isSsoLockedOut: isSsoLockedOutAdminSetting, isLoading: isSsoLockedAdminSettingLoading } =
    useSsoLockedOutStatus(thirdPartyOrg?.id ?? '');

  const isAttemptSSOLogin = location.query.attemptSSOLogin === 'true';
  const isUserAuthenticated = isAuthenticatedUser();
  const { isJit, isLoading: isJitLoading } = useIsJitOrganization({
    orgId: thirdPartyOrg?.id,
    skip: !isAttemptSSOLogin || !isUserAuthenticated,
  });

  const { enableProgramAutoJoin, autoJoinSettled, isMembershipLoading, membership } =
    useProgramAutoJoinDomainAllowList();

  // Wait for all required data to load before making decisions
  if (isSsoLockedAdminSettingLoading || (isAttemptSSOLogin && isUserAuthenticated && isJitLoading)) {
    return;
  }
  if (
    isOrgScopedHomeEnabled &&
    !isProgramLoading &&
    program &&
    thirdPartyOrg?.slug &&
    location.pathname.startsWith('/programs/')
  ) {
    const programSlug = program.slug;
    const programPrefix = `/programs/${programSlug}`;
    if (location.pathname === programPrefix || location.pathname.startsWith(programPrefix + '/')) {
      // Domain-allowlist: hold the redirect until the auto-join (fired by the effect above) settles. Also
      // hold while membership is still loading, so we don't bounce a (possibly allowlisted) learner before
      // the join can run. Non-allowlist learners fall through and redirect now. We redirect on settle
      // regardless of join success or failure (see the effect comment above).
      if (enableProgramAutoJoin && !autoJoinSettled && (isMembershipLoading || membership?.isAllowlisted)) {
        return null;
      }
      const subpath = location.pathname.slice(programPrefix.length) || '';
      // /browse maps to /search; domain category routes (/business, /computer-science, etc.)
      // map to org-scoped browse routes (/browse/:domainSlug).
      const subpathBase = subpath.split('/')[1] || '';
      const BROWSE_CATEGORY_SLUGS = new Set([
        'arts-and-humanities',
        'business',
        'computer-science',
        'data-science',
        'information-technology',
        'health',
        'math-and-logic',
        'personal-development',
        'physical-science-and-engineering',
        'social-sciences',
        'language-learning',
      ]);
      const isBrowseRoot = subpathBase === 'browse';
      const isBrowseCategoryRoute = BROWSE_CATEGORY_SLUGS.has(subpathBase);
      let orgPath: string;
      if (isBrowseRoot) {
        orgPath = `/organizations/${thirdPartyOrg.slug}/search`;
      } else if (isBrowseCategoryRoute) {
        orgPath = `/organizations/${thirdPartyOrg.slug}/browse/${subpathBase}`;
      } else {
        orgPath = `/organizations/${thirdPartyOrg.slug}${subpath}`;
      }
      let searchPart = normalizeSearchString(location.search || '');
      // Append programId for deep link redirects so the org-scoped XDP knows which program
      // to use for enrollment (read by EnterpriseProgramAndMembershipDataProvider).
      const hasDeepLinkParams =
        location.query.showMiniModal || location.query.deepLinkItemId || location.query.productId;
      if (hasDeepLinkParams && program?.id) {
        const params = new URLSearchParams(searchPart ? searchPart.replace(/^\?/, '') : '');
        params.set('programId', program.id);
        searchPart = '?' + params.toString().replace(/\+/g, '%20');
      }
      redirectClientOrServer(orgPath + searchPart);

      // the guard from continuing to evaluate SSO/auth logic on the old program-scoped URL
      return null;
    }
  }

  const isLearnerPartOfLfoEnabledOrg = location.query.isExternal === 'true';
  const isOrgWhiteListedForSSOLogin =
    features?.whitelistOrganizationForSSOLogin || features?.whitelistOrganizationProgramsForSSOLogin;
  const isSsoLockedOutEnterprise = features?.enableUnifiedSso
    ? isSsoLockedOutAdminSetting
    : features?.enableForceSsoLogin || features?.enableForceSsoLoginV2Beta;
  const shouldRedirectToIdpOnOrg =
    isLearnerPartOfLfoEnabledOrg && isSsoLockedOutEnterprise && isOrgWhiteListedForSSOLogin;

  /**
   * When attempting SSO login, always redirect if user is not authenticated,
   * or if it's a JIT organization and the user is not within the SSO grace period
   */
  const shouldRedirectForAttemptSSO =
    isAttemptSSOLogin && (!isUserAuthenticated || (isJit && !isWithinSsoGracePeriod(thirdPartyOrg?.id ?? '')));
  const shouldRedirectToIdp = shouldRedirectForAttemptSSO || (!isUserAuthenticated && shouldRedirectToIdpOnOrg);

  if (shouldRedirectToIdp && thirdPartyOrg && thirdPartyOrg.loginMethod === 'SAML') {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrg.id} />;
  }

  const shouldRedirectToLoginForLfo =
    !isAuthenticatedUser() &&
    isFeaturesLoading === false &&
    location.query.isExternal &&
    !location.query.attemptSSOLogin;

  if (shouldRedirectToLoginForLfo) {
    redirectClientOrServer('/login?redirectTo=' + encodeURIComponent(location.pathname + location.search));
    return null;
  }

  // Show program archived page if program is archived.
  if (
    !isProgramLoading &&
    program &&
    (program?.endedAt || (enterpriseContracts && allContractsHardPausedOrTerminated(enterpriseContracts)))
  ) {
    return <ProgramArchivedDenyModal userId={user.get()?.id} naptime={naptime} />;
  }

  // If no program found, render NotFound page.
  if (!isProgramLoading && !program) {
    if (inServerContext) {
      throw new NotFoundError();
    } else {
      return <NotFoundPage />;
    }
  }

  return <React.Fragment>{children}</React.Fragment>;
};

/**
 * @description Checks data required to serve an enterprise page, if said data is not present returns appropriate error page.
 */
export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterprisePageGuard);
